import React, { useEffect, useMemo, useState } from "react";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { useReportJobStore } from "../../store/useReportJobStore";
import type { ReportJob } from "../../types/reportJob";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { ChevronDown, Download, Plus, RefreshCw, Trash2 } from "lucide-react";
import { apiService } from "../../services/api";

export const ReportsJobsList: React.FC = () => {
  const navigateToView = useAppNavigate();
  const {
    jobs,
    loading,
    currentPage,
    itemsPerPage,
    totalPages,
    fetchJobs,
    retryJob,
    deleteJob,
  } = useReportJobStore();
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);
  const [retryingJobId, setRetryingJobId] = useState<number | null>(null);

  useEffect(() => {
    fetchJobs({ page: 1, limit: 20 }).catch(console.error);
  }, [fetchJobs]);

  const sortedJobs = useMemo(
    () => [...jobs].sort((a, b) => b.id - a.id),
    [jobs],
  );

  const handleDownload = async (jobId: number) => {
    try {
      const downloadInfo = await apiService.downloadReportJob(jobId);
      window.location.assign(downloadInfo.download_url);
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : "Failed to download report file",
      );
    }
  };

  const handlePageChange = (nextPage: number) => {
    fetchJobs({ page: nextPage, limit: itemsPerPage }).catch(console.error);
  };

  const handleRetry = async (jobId: number) => {
    try {
      setRetryingJobId(jobId);
      await retryJob(jobId);
    } catch (error) {
      alert(
        error instanceof Error ? error.message : "Failed to retry report job",
      );
    } finally {
      setRetryingJobId(null);
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return "-";
    return new Date(value).toLocaleString();
  };

  const getStatusClass = (status: ReportJob["status"]) => {
    if (status === "completed") return "text-green-700 bg-green-100";
    if (status === "failed") return "text-red-700 bg-red-100";
    return "text-yellow-700 bg-yellow-100";
  };

  const formatExportType = (exportType: ReportJob["export_type"]) => {
    if (exportType === "summary_per_vehicle") {
      return "Summary per vehicles";
    }
    if (exportType === "summary") {
      return "Summary";
    }
    return "Raw data";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-600 mt-1">
            Create and manage generated report jobs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              fetchJobs({ page: currentPage, limit: itemsPerPage })
            }
            className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={() => navigateToView("report-create")}
            className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Report
          </button>
        </div>
      </div>

      {loading && jobs.length === 0 ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Export Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Grouping
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Started
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedJobs.map((job) => {
                const isExpanded = expandedJobId === job.id;
                return (
                  <React.Fragment key={job.id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() =>
                        setExpandedJobId(isExpanded ? null : (job.id ?? null))
                      }
                    >
                      <td className="px-4 py-3">
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        #{job.id}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatExportType(job.export_type)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {job.grouping || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(
                            job.status,
                          )}`}
                        >
                          {job.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(job.started_at)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          {job.status === "completed" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(job.id);
                              }}
                              className="inline-flex items-center gap-1 text-green-700 hover:text-green-900"
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </button>
                          )}
                          {job.status === "failed" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRetry(job.id).catch(console.error);
                              }}
                              className="text-amber-700 hover:text-amber-900 disabled:opacity-60"
                              disabled={retryingJobId === job.id}
                            >
                              {retryingJobId === job.id
                                ? "Retrying..."
                                : "Retry"}
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                window.confirm(
                                  "Delete this report job from the list?",
                                )
                              ) {
                                deleteJob(job.id).catch(console.error);
                              }
                            }}
                            className="inline-flex items-center gap-1 text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="font-medium text-gray-700">
                                Job Details
                              </p>
                              <p className="text-gray-600 mt-1">
                                Rows: {job.row_count ?? "-"}
                              </p>
                              <p className="text-gray-600">
                                Completed: {formatDate(job.completed_at)}
                              </p>
                              {job.error_message && (
                                <p className="text-red-600 mt-1">
                                  Error: {job.error_message}
                                </p>
                              )}
                              {job.status === "failed" && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRetry(job.id).catch(console.error);
                                  }}
                                  className="mt-2 text-amber-700 hover:text-amber-900 disabled:opacity-60"
                                  disabled={retryingJobId === job.id}
                                >
                                  {retryingJobId === job.id
                                    ? "Retrying..."
                                    : "Retry failed job"}
                                </button>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-700">
                                Applied Filters
                              </p>
                              <p className="text-gray-600 mt-1">
                                Date: {job.filters.start_date} -{" "}
                                {job.filters.end_date}
                              </p>
                              <p className="text-gray-600">
                                Country:{" "}
                                {job.filters.country_codes?.length
                                  ? job.filters.country_codes.join(", ")
                                  : "All"}
                              </p>
                              <p className="text-gray-600">
                                Vehicles:{" "}
                                {job.filters.bike_ids?.length
                                  ? job.filters.bike_ids.join(", ")
                                  : job.filters.vehicle_search || "All"}
                              </p>
                              <p className="text-gray-600">
                                Categories:{" "}
                                {job.filters.category_ids?.length
                                  ? job.filters.category_ids.join(", ")
                                  : "All"}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {sortedJobs.length === 0 && (
            <div className="p-8 text-center text-sm text-gray-500">
              No report jobs yet. Create your first report.
            </div>
          )}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            disabled={currentPage <= 1}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50"
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            disabled={currentPage >= totalPages}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50"
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};
